import { useState, useEffect, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Search, Phone, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Filter, Loader2 } from "lucide-react";
import { chatbotService, type ChatSessionSummaryDto, type ChatSessionDetailDto } from "@/services/chatbotService";
import { toast } from "sonner";
import { useProjectSelection } from "@/contexts/ProjectSelectionContext";

interface CallRecord {
  id: string;
  caller: string;
  phone: string;
  timestamp: string;
  summary: string;
  channel: "Phone" | "SMS";
  sentiment: string;
  duration: string;
  transcript: string;
  fullSummary: string;
  reason: string;
  actions: string[];
  messages?: Array<{ role: string; content: string; timestamp?: string; order?: number; source?: string, isVoice?: boolean }>;
}

const formatDuration = (seconds: number): string => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.floor(seconds % 60);
  return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
};

const parseActions = (action?: string): string[] => {
  if (!action) return [];
  try {
    const parsed = JSON.parse(action);
    if (Array.isArray(parsed)) {
      return parsed.map(String);
    }
    return [String(parsed)];
  } catch {
    return [action];
  }
};

const PAGE_SIZE = 25;

const mapSessionToCallRecord = (session: ChatSessionSummaryDto): CallRecord => {
  return {
    id: session.id,
    caller: session.caller || "Unknown Caller", // Fallback if caller not provided
    phone: session.userId || "Unknown",
    timestamp: new Date(session.createdAt).toLocaleString(),
    summary: session.summary || "No summary available",
    channel: "Phone", // Defaulting to Phone as per requirement context, or add logic if backend provides channel
    sentiment: session.sentiment || "Neutral",
    duration: formatDuration(session.duration || 0),
    transcript: "", // Will be loaded on expand
    fullSummary: session.summary || "",
    reason: session.reason || "General Inquiry",
    actions: parseActions(session.action),
    messages: []
  };
};

export default function ChatHistory() {
  const { resolveProjectId } = useProjectSelection();
  const [calls, setCalls] = useState<CallRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPageLoading, setIsPageLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [sentimentFilter, setSentimentFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loadingDetails, setLoadingDetails] = useState<Record<string, boolean>>({});

  const loadSessions = useCallback(async (page: number = 1) => {
    try {
      const isInitialPage = page === 1;
      if (isInitialPage) {
        setIsLoading(true);
      } else {
        setIsPageLoading(true);
      }
      const projectId = resolveProjectId();
      const offset = (page - 1) * PAGE_SIZE;
      const sessions = await chatbotService.listSessions({ limit: PAGE_SIZE, offset, projectId });
      const mappedCalls: CallRecord[] = sessions.map(mapSessionToCallRecord);
      setCalls(mappedCalls);
      setHasNextPage(sessions.length === PAGE_SIZE);
      setExpandedId(null);
    } catch (error) {
      console.error("Failed to load sessions:", error);
      toast.error("Failed to load chat history");
    } finally {
      setIsLoading(false);
      setIsPageLoading(false);
    }
  }, [resolveProjectId]);

  useEffect(() => {
    setCurrentPage(1);
    loadSessions(1);
  }, [loadSessions]);

  const goToPage = async (page: number) => {
    if (page < 1 || page === currentPage || isPageLoading) return;
    setCurrentPage(page);
    await loadSessions(page);
  };

  const handleExpand = async (id: string, isOpen: boolean) => {
    setExpandedId(isOpen ? id : null);
    
    if (isOpen) {
      // Check if we already have the transcript/messages
      const call = calls.find(c => c.id === id);
      if (call && (!call.messages || call.messages.length === 0) && !call.transcript) {
        await loadSessionDetails(id);
      }
    }
  };

  const loadSessionDetails = async (id: string) => {
    setLoadingDetails(prev => ({ ...prev, [id]: true }));
    try {
      const details = await chatbotService.getSession(id);
      setCalls(prevCalls => prevCalls.map(call => {
        if (call.id === id) {
          // Format transcript from messages if needed, or use what's provided
          const transcript = details.messages
            ? details.messages.map(m => `${m.role}: ${m.content}`).join('\n\n')
            : "No transcript available";
            
          return {
            ...call,
            transcript,
            messages: details.messages,
            // Update other fields if details provide more info
            summary: details.summary || call.summary,
            fullSummary: details.summary || call.fullSummary,
            sentiment: details.sentiment || call.sentiment,
            reason: details.reason || call.reason,
            actions: details.action ? parseActions(details.action) : call.actions
          };
        }
        return call;
      }));
    } catch (error) {
      console.error(`Failed to load details for session ${id}:`, error);
      toast.error("Failed to load call details");
    } finally {
      setLoadingDetails(prev => ({ ...prev, [id]: false }));
    }
  };

  const getSentimentColor = (sentiment: string) => {
    // Normalize sentiment string case
    const normalized = sentiment.toLowerCase();
    if (normalized.includes("positive")) return "bg-status-success/10 text-status-success border-status-success/20";
    if (normalized.includes("neutral")) return "bg-status-warning/10 text-status-warning border-status-warning/20";
    if (normalized.includes("negative")) return "bg-status-pending/10 text-status-pending border-status-pending/20";
    if (normalized.includes("frustrated") || normalized.includes("angry")) return "bg-status-error/10 text-status-error border-status-error/20";
    
    return "bg-secondary text-secondary-foreground";
  };

  const filteredCalls = calls.filter(call => {
    const matchesSearch = call.caller.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         call.phone.includes(searchQuery) ||
                         call.summary.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesSentiment = sentimentFilter === "all" || (call.sentiment && call.sentiment.toLowerCase() === sentimentFilter.toLowerCase());
    return matchesSearch && matchesSentiment;
  });

  return (
    <div className="space-y-6 pb-20 lg:pb-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <p className="text-muted-foreground mt-1">View and analyze all customer interactions</p>
        </div>
        {/* <Button variant="outline">
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button> */}
      </div>

      {/* Filters */}
      <Card className="p-4 card-hover">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by caller name, phone, or keywords..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={sentimentFilter} onValueChange={setSentimentFilter}>
            <SelectTrigger className="w-full md:w-[200px]">
              <Filter className="w-4 h-4 mr-2" />
              <SelectValue placeholder="Filter by sentiment" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sentiments</SelectItem>
              <SelectItem value="Positive">Positive</SelectItem>
              <SelectItem value="Neutral">Neutral</SelectItem>
              <SelectItem value="Negative">Negative</SelectItem>
              <SelectItem value="Frustrated">Frustrated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </Card>

      {/* Loading State */}
      {isLoading ? (
        <Card className="p-12 text-center">
          <Loader2 className="w-12 h-12 mx-auto mb-4 text-primary animate-spin" />
          <p className="text-muted-foreground">Loading chat history...</p>
        </Card>
      ) : (
        <>
          {/* Desktop Table View */}
          <div className="hidden lg:block">
            <Card className="card-hover overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="text-left p-4 font-semibold">Caller</th>
                      <th className="text-left p-4 font-semibold">Timestamp</th>
                      <th className="text-left p-4 font-semibold">Call Duration</th>
                      <th className="text-left p-4 font-semibold">Summary</th>
                      <th className="text-left p-4 font-semibold">Sentiment</th>
                      <th className="text-left p-4 font-semibold w-[50px]"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredCalls.map((call) => (
                      <Collapsible
                        key={call.id}
                        open={expandedId === call.id}
                        onOpenChange={(open) => handleExpand(call.id, open)}
                        asChild
                      >
                        <>
                          <CollapsibleTrigger asChild>
                            <tr className="border-b hover:bg-accent/50 cursor-pointer transition-colors">
                              <td className="p-4">
                                <div>
                                  <p className="font-medium">{call.caller}</p>
                                  <p className="text-sm text-muted-foreground">{call.phone}</p>
                                </div>
                              </td>
                              <td className="p-4">
                                <div className="text-sm">
                                  <p>{call.timestamp.split(',')[0]}</p>
                                  <p className="text-muted-foreground">{call.timestamp.split(',')[1]}</p>
                                </div>
                              </td>
                              <td className="p-4">
                                <p className="text-sm font-medium">{call.duration}</p>
                              </td>
                              <td className="p-4">
                                <p className="text-sm line-clamp-2">{call.summary}</p>
                              </td>
                              <td className="p-4">
                                <Badge className={getSentimentColor(call.sentiment)}>
                                  {call.sentiment}
                                </Badge>
                              </td>
                              <td className="p-4">
                                {expandedId === call.id ? (
                                  <ChevronUp className="w-4 h-4 text-muted-foreground" />
                                ) : (
                                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                                )}
                              </td>
                            </tr>
                          </CollapsibleTrigger>
                          <CollapsibleContent asChild>
                            <tr>
                              <td colSpan={6} className="p-0">
                                <div className="bg-muted/30 p-6 space-y-6">
                                  {loadingDetails[call.id] ? (
                                    <div className="flex justify-center p-4">
                                      <Loader2 className="w-6 h-6 animate-spin text-primary" />
                                    </div>
                                  ) : (
                                    /* Call Details Grid */
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                      {/* Full Transcript */}
                                      <div>
                                        <h4 className="font-semibold mb-3 flex items-center gap-2">
                                          Full Transcript
                                          <Badge variant="secondary" className="text-xs">{call.duration}</Badge>
                                        </h4>
                                        <div className="bg-background rounded-lg p-4 max-h-[400px] overflow-y-auto">
                                          <pre className="text-sm whitespace-pre-wrap font-sans">{call.transcript || "No transcript available."}</pre>
                                        </div>
                                      </div>

                                      {/* Call Summary & Details */}
                                      <div className="space-y-4">
                                        <div>
                                          <h4 className="font-semibold mb-2">Call Summary</h4>
                                          <p className="text-sm text-muted-foreground bg-background rounded-lg p-4">
                                            {call.fullSummary || "No summary available."}
                                          </p>
                                        </div>

                                        <div>
                                          <h4 className="font-semibold mb-2">Reason Detected</h4>
                                          <Badge variant="secondary" className="text-sm">
                                            {call.reason}
                                          </Badge>
                                        </div>

                                        <div>
                                          <h4 className="font-semibold mb-2">Actions Taken</h4>
                                          {call.actions.length > 0 ? (
                                            <ul className="space-y-2">
                                              {call.actions.map((action, idx) => (
                                                <li key={idx} className="flex items-start gap-2 text-sm">
                                                  <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                                  <span>{action}</span>
                                                </li>
                                              ))}
                                            </ul>
                                          ) : (
                                            <p className="text-sm text-muted-foreground">No actions recorded.</p>
                                          )}
                                        </div>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </td>
                            </tr>
                          </CollapsibleContent>
                        </>
                      </Collapsible>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

          {/* Mobile Card View */}
          <div className="lg:hidden space-y-4">
            {filteredCalls.map((call) => (
              <Collapsible
                key={call.id}
                open={expandedId === call.id}
                onOpenChange={(open) => handleExpand(call.id, open)}
              >
                <Card className="card-hover">
                  <CollapsibleTrigger asChild>
                    <div className="p-4 cursor-pointer">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <p className="font-semibold">{call.caller}</p>
                          <p className="text-sm text-muted-foreground">{call.phone}</p>
                        </div>
                        {expandedId === call.id ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                      
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <Badge className={getSentimentColor(call.sentiment)}>
                          {call.sentiment}
                        </Badge>
                        <span className="text-xs text-muted-foreground">{call.timestamp}</span>
                      </div>

                      <p className="text-sm text-muted-foreground line-clamp-2">{call.summary}</p>
                    </div>
                  </CollapsibleTrigger>

                  <CollapsibleContent>
                    <div className="border-t p-4 bg-muted/30 space-y-4">
                      {loadingDetails[call.id] ? (
                        <div className="flex justify-center p-4">
                          <Loader2 className="w-6 h-6 animate-spin text-primary" />
                        </div>
                      ) : (
                        <>
                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Full Transcript</h4>
                            <div className="bg-background rounded-lg p-3 max-h-[300px] overflow-y-auto">
                              <pre className="text-xs whitespace-pre-wrap font-sans">{call.transcript || "No transcript available."}</pre>
                            </div>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Call Summary</h4>
                            <p className="text-sm text-muted-foreground">{call.fullSummary}</p>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Reason</h4>
                            <Badge variant="secondary">{call.reason}</Badge>
                          </div>

                          <div>
                            <h4 className="font-semibold mb-2 text-sm">Actions Taken</h4>
                            {call.actions.length > 0 ? (
                              <ul className="space-y-1.5">
                                {call.actions.map((action, idx) => (
                                  <li key={idx} className="flex items-start gap-2 text-sm">
                                    <span className="w-1.5 h-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                                    <span>{action}</span>
                                  </li>
                                ))}
                              </ul>
                            ) : (
                              <p className="text-sm text-muted-foreground">No actions recorded.</p>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            ))}
          </div>

          {filteredCalls.length === 0 && (
            <Card className="p-12 text-center">
              <Phone className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-20" />
              <p className="text-muted-foreground">No calls found matching your filters</p>
            </Card>
          )}

          <div className="flex items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Page {currentPage}
              {isPageLoading ? " - Loading..." : ""}
            </p>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage <= 1 || isPageLoading}
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={!hasNextPage || isPageLoading}
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
